import { atom, useAtom } from 'jotai';
import { MatrixClient, Method } from 'matrix-js-sdk';
import { atomFamily } from 'jotai/utils';
import { useCallback } from 'react';

export type RoomProcessInfo = {
  process_action: number;
  process_state: number;
};

export const roomProcessInfoAtomFamily = atomFamily<string, RoomProcessInfo | null>(
  () => atom<RoomProcessInfo | null>(null)
);

export const useRoomProcessInfo = (mx: MatrixClient, roomId: string) => {
  const [info, setInfo] = useAtom(roomProcessInfoAtomFamily(roomId));

  const fetchInfo = useCallback(async () => {
    try {
      const processInfo: RoomProcessInfo = await mx.http.authedRequest(
        Method.Get,
        `/rooms/${roomId}/process_info`
      );
      setInfo(processInfo);
      return processInfo;
    } catch (error) {
      // if (error.httpStatus !== 404) {
      //   console.error('Failed to fetch room process info', error);
      // }
      setInfo(null);
      return null;
    }
  }, [mx, roomId, setInfo]);

  const closeCase = useCallback(async () => {
    const newInfo: RoomProcessInfo = {
      process_action: 3,
      process_state: 2,
    };
    await mx.http.authedRequest(
      Method.Put,
      `/rooms/${roomId}/process_info`,
      undefined,
      newInfo
    );
  }, [mx, roomId]);

  return { info, fetchInfo, closeCase };
};

// Atom to store all rooms' process info, mapped by roomId
export const roomsProcessInfoAtom = atom<Record<string, RoomProcessInfo | null>>({});

// Hook to fetch and access process info for multiple rooms
export const useRoomsProcessInfo = (mx: MatrixClient, roomIds: string[]) => {
  const [roomsInfo, setRoomsInfo] = useAtom(roomsProcessInfoAtom);

  const fetchInfoForRooms = useCallback(async () => {
    if (roomIds.length === 0) return;

    const newRoomsInfo: Record<string, RoomProcessInfo | null> = { ...roomsInfo };
    let hasUpdate = false;
    
    for (const roomId of roomIds) {
      if (typeof newRoomsInfo[roomId] !== 'undefined') continue;

      try {
        const processInfo: RoomProcessInfo = await mx.http.authedRequest(
          Method.Get,
          `/rooms/${roomId}/process_info`
        );
        newRoomsInfo[roomId] = processInfo;
        hasUpdate = true;
      } catch (error) {
        newRoomsInfo[roomId] = null;
        hasUpdate = true;
      }
    }
    if (hasUpdate) {
      setRoomsInfo(newRoomsInfo);
    }
  }, [mx, roomIds, setRoomsInfo, roomsInfo]);

  return { roomsInfo, fetchInfoForRooms };
};
